import React, { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import ComparisonLanding from "../../components/Dashboard/Compare/ComparisonLanding";
import ComparisonLayout from "../../components/Dashboard/Compare/ComparisonLayout";
import { useMembership } from "../../contexts/MembershipContext";
import { useAnalytics } from "../../hooks/useAnalytics";

const FREE_TIER_SYMBOLS = new Set(["AAPL", "NVDA", "MSFT", "GOOGL", "AMZN"]);

const isPermitted = (ticker: string | null, membershipActive: boolean) =>
  !ticker || membershipActive || FREE_TIER_SYMBOLS.has(ticker.toUpperCase());

const ComparisonPage = () => {
  const location = useLocation();
  const { isMembershipActive } = useMembership();
  const initialTicker =
    (location.state as { initialTicker?: string } | null)?.initialTicker ?? null;

  // The tickers live in the URL, not component state: entering a comparison
  // from the landing pushes ONE history entry, so the back button returns to
  // the compare landing instead of skipping to whichever page preceded
  // /compare. Slot edits inside the layout replace, so back never has to walk
  // through every selection. Refresh and share work for free.
  const [searchParams, setSearchParams] = useSearchParams();
  const leftTicker = searchParams.get("left");
  const rightTicker = searchParams.get("right");

  const setTickers = (left: string | null, right: string | null) => {
    const params = new URLSearchParams();
    if (left) params.set("left", left);
    if (right) params.set("right", right);
    const wasLanding = !searchParams.has("left") && !searchParams.has("right");
    setSearchParams(params, { replace: !wasLanding });
  };

  const setLeftTicker = (ticker: string | null) => setTickers(ticker, rightTicker);
  const setRightTicker = (ticker: string | null) => setTickers(leftTicker, ticker);

  // "Compare" jump from a ticker page: seed the left slot without creating a
  // history entry (the landing was never shown, so back should leave /compare).
  useEffect(() => {
    if (initialTicker && !leftTicker && !rightTicker) {
      setSearchParams({ left: initialTicker }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTicker]);

  // Guard: clear any tickers that a non-member isn't permitted to view
  useEffect(() => {
    if (isMembershipActive) return;
    const permittedLeft = isPermitted(leftTicker, false) ? leftTicker : null;
    const permittedRight = isPermitted(rightTicker, false) ? rightTicker : null;
    if (permittedLeft !== leftTicker || permittedRight !== rightTicker) {
      setTickers(permittedLeft, permittedRight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMembershipActive, leftTicker, rightTicker]);

  // Feature usage: a completed side-by-side comparison (both slots filled). Keyed on
  // the pair so it fires once per distinct comparison.
  const { trackFeature } = useAnalytics();
  useEffect(() => {
    if (leftTicker && rightTicker) {
      trackFeature("stocks_compared", { tickers: [leftTicker, rightTicker] });
    }
  }, [leftTicker, rightTicker, trackFeature]);

  // Members: clear only that slot and stay in the layout
  // Free tier: clear both and return to landing
  const handleClearLeft = () => {
    setTickers(null, isMembershipActive ? rightTicker : null);
  };

  const handleClearRight = () => {
    setTickers(isMembershipActive ? leftTicker : null, null);
  };

  if (!leftTicker && !rightTicker) {
    return <ComparisonLanding onSelectStock={setLeftTicker} onSelectRight={setRightTicker} />;
  }

  return (
    <ComparisonLayout
      leftTicker={leftTicker}
      rightTicker={rightTicker}
      onClearLeft={handleClearLeft}
      onClearRight={handleClearRight}
      onSelectLeft={setLeftTicker}
      onSelectRight={setRightTicker}
    />
  );
};

export default ComparisonPage;
